import { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import api from "@/lib/api";
import { StudioPage, Panel, SectionLabel, Metric, Loading, cx } from "./StudioShared";
import { TrendingUp, Clock, Coins, DollarSign, Info } from "lucide-react";

const money2 = (c) => `$${Number(c || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function RoiDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/analytics/roi").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (d === null) return <Loading label="Calculating ROI…" />;
  if (!d) return <div className="text-slate-500 py-10 text-center">No data yet.</div>;
  const deptChart = (d.by_department_cost || []).filter((x) => x.cost > 0).slice(0, 8);

  return (
    <StudioPage title="ROI Dashboard" testid="roi-dashboard-page"
      question="Not just what AI costs — what it's worth. Value created vs. dollars spent.">
      {/* Hero */}
      <Panel className="p-8 mb-6 relative overflow-hidden" data-testid="roi-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent pointer-events-none" />
        <div className="relative grid md:grid-cols-4 gap-6">
          <HeroStat label="AI Investment" value={money2(d.cost)} icon={Coins} sub={`${d.executions} executions`} />
          <HeroStat label="Est. Hours Saved" value={d.hours_saved} icon={Clock} sub={`${d.time_saved_this_month_hours}h this month`} />
          <HeroStat label="Est. Labor Value" value={money2(d.labor_value)} icon={DollarSign} sub="value created" />
          <HeroStat label="Estimated ROI" value={`${d.roi_pct.toLocaleString()}%`} icon={TrendingUp} accent sub="return on AI spend" />
        </div>
      </Panel>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Metric label="Cost / Completed Run" value={`$${Number(d.cost_per_workflow_run || 0).toFixed(4)}`} testid="roi-cost-per-run" />
        <Metric label="Completed Runs" value={d.completed_runs} testid="roi-completed" />
        <Metric label="Proposals Assisted" value={d.proposals_assisted} testid="roi-proposals" />
        <Metric label="Executions" value={d.executions} testid="roi-executions" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel className="p-5 lg:col-span-2" data-testid="roi-dept-chart">
          <SectionLabel className="mb-4">AI Cost by Department</SectionLabel>
          {deptChart.length === 0 ? <p className="text-slate-500 text-sm">No department-tagged workflow runs yet.</p> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={deptChart} margin={{ left: -10 }}>
                <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0A1128", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4 }} formatter={(v) => `$${Number(v).toFixed(4)}`} />
                <Bar dataKey="cost" radius={[4, 4, 0, 0]}>{deptChart.map((_, i) => <Cell key={i} fill="#D4AF37" />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
        <Panel className="p-5" data-testid="roi-assumptions">
          <SectionLabel className="mb-3">Estimate Assumptions</SectionLabel>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-slate-400">Minutes saved / execution</span><span className="text-white font-mono">{d.assumptions.minutes_saved_per_execution}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Labor rate / hour</span><span className="text-white font-mono">${d.assumptions.labor_rate_usd_per_hour}</span></div>
          </div>
          <div className="mt-4 flex items-start gap-2 text-xs text-slate-500 border-t border-white/10 pt-3">
            <Info size={13} className="text-gold shrink-0 mt-0.5" /><span>{d.assumptions.note}</span>
          </div>
        </Panel>
      </div>
    </StudioPage>
  );
}

const HeroStat = ({ label, value, icon: Icon, sub, accent }) => (
  <div>
    <div className="flex items-center gap-2 mb-2"><Icon size={16} className={accent ? "text-emerald-400" : "text-gold"} /><span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">{label}</span></div>
    <div className={cx("font-mono text-4xl font-light tracking-tight leading-none", accent ? "text-emerald-400" : "text-white")}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
  </div>
);
